import { AppRouteRecordRaw } from '../types';
import { PARTNER_TYPES } from '@/constants/partner-types';
import { Roles } from '@/constants/roles';
import { h } from 'vue';
import { RouterView } from 'vue-router';

const Body = () => import('@/components/body.vue');
const RoutePassThrough = { render: () => h(RouterView) };
const PlatformStatusAndAlertsOverview = () =>
  import('@/pages/platform-status-and-alerts/overview/PlatformStatusAndAlertsOverview.vue');
const PartnerAlertSlackChannelsPage = () =>
  import('@/pages/platform-status-and-alerts/alert-slack-channels/PartnerAlertSlackChannelsPage.vue');
const PartnerAlertsPage = () =>
  import('@/pages/platform-status-and-alerts/alerts/PartnerAlertsPage.vue');
const PartnerIncidentsPage = () =>
  import('@/pages/platform-status-and-alerts/incidents/PartnerIncidentsPage.vue');
const PlatformStatusScheduledMaintenance = () =>
  import('@/pages/platform-status-and-alerts/scheduled-maintenance/PlatformStatusScheduledMaintenance.vue');
const PlatformStatusCurrentStatus = () =>
  import('@/pages/platform-status-and-alerts/current-status/PlatformStatusCurrentStatus.vue');
const PartnerAnnouncementsPage = () =>
  import('@/pages/platform-status-and-alerts/announcements/PartnerAnnouncementsPage.vue');
const AnnouncementsListPage = () =>
  import('@/pages/platform-status-and-alerts/admin/announcements/list/AnnouncementsListPage.vue');
const AnnouncementCreatePage = () =>
  import('@/pages/platform-status-and-alerts/admin/announcements/create/AnnouncementCreatePage.vue');
const AnnouncementEditPage = () =>
  import('@/pages/platform-status-and-alerts/admin/announcements/edit/AnnouncementEditPage.vue');
const AlertsListPage = () =>
  import('@/pages/platform-status-and-alerts/admin/alerts/list/AlertsListPage.vue');
const AlertCreatePage = () =>
  import('@/pages/platform-status-and-alerts/admin/alerts/create/AlertCreatePage.vue');
const AlertEditPage = () =>
  import('@/pages/platform-status-and-alerts/admin/alerts/edit/AlertEditPage.vue');
const IncidentsListPage = () =>
  import('@/pages/platform-status-and-alerts/admin/incidents/list/IncidentsListPage.vue');
const IncidentCreatePage = () =>
  import('@/pages/platform-status-and-alerts/admin/incidents/create/IncidentCreatePage.vue');
const IncidentEditPage = () =>
  import('@/pages/platform-status-and-alerts/admin/incidents/edit/IncidentEditPage.vue');
const MaintenanceListPage = () =>
  import('@/pages/platform-status-and-alerts/admin/scheduled-maintenance/list/MaintenanceListPage.vue');
const MaintenanceCreatePage = () =>
  import('@/pages/platform-status-and-alerts/admin/scheduled-maintenance/create/MaintenanceCreatePage.vue');
const MaintenanceEditPage = () =>
  import('@/pages/platform-status-and-alerts/admin/scheduled-maintenance/edit/MaintenanceEditPage.vue');
const MaintenanceAutoTransitionPage = () =>
  import('@/pages/platform-status-and-alerts/admin/maintenance-auto-transition/MaintenanceAutoTransitionPage.vue');
const DeliveryHistoryPage = () =>
  import('@/pages/platform-status-and-alerts/admin/delivery-history/DeliveryHistoryPage.vue');
const PartnerOperationalAlertsPage = () =>
  import('@/pages/platform-status-and-alerts/operational-alerts/PartnerOperationalAlertsPage.vue');
const OperationalExcellenceConfigsPage = () =>
  import('@/pages/platform-status-and-alerts/operational-excellence/OperationalExcellenceConfigsPage.vue');
const OperationalExcellenceConfigEditPage = () =>
  import('@/pages/platform-status-and-alerts/operational-excellence/edit/OperationalExcellenceConfigEditPage.vue');
const OperationalExcellenceHistoryPage = () =>
  import('@/pages/platform-status-and-alerts/operational-excellence/history/OperationalExcellenceHistoryPage.vue');
const PlatformStatusSystemMetrics = () =>
  import('@/pages/platform-status-and-alerts/system-metrics/PlatformStatusSystemMetrics.vue');

const ADMIN_META = {
  requiresAuth: true,
  requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
  requiredRoles: [Roles.platform_communications_admin],
  requiredUserGroup: ['ryd'],
};

export const platformStatusRoutes: AppRouteRecordRaw[] = [
  {
    path: '/platform-status-and-alerts',
    component: Body,
    meta: { breadcrumb: 'Platform Status & Alerts' },
    children: [
      {
        path: 'overview',
        name: 'platformStatusAndAlertsOverview',
        component: PlatformStatusAndAlertsOverview,
        meta: {
          title: 'Platform Status & Alerts Overview | ryd Portal',
          requiresAuth: true,
          breadcrumb: 'Overview',
        },
      },
      {
        path: 'alert-slack-channels',
        name: 'partnerSelfAlertSlackChannels',
        component: PartnerAlertSlackChannelsPage,
        meta: {
          title: 'Alert Slack Channels | ryd Portal',
          requiresAuth: true,
          requiredRoles: [Roles.platform_communications_admin],
          breadcrumb: 'Alert Slack Channels',
        },
      },
      {
        path: 'operational-excellence',
        name: 'operationalExcellenceAlertConfigs',
        component: OperationalExcellenceConfigsPage,
        meta: {
          title: 'Operational Excellence Alerts | ryd Portal',
          requiresAuth: true,
          requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Operational Excellence Alerts',
        },
      },
      {
        path: 'operational-excellence/new',
        name: 'operationalExcellenceAlertConfigNew',
        component: OperationalExcellenceConfigEditPage,
        meta: {
          title: 'Add Partner Alerts | ryd Portal',
          requiresAuth: true,
          requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Operational Excellence Alerts — Add Partner',
        },
      },
      {
        path: 'operational-excellence/edit/:partnerKey',
        name: 'operationalExcellenceAlertConfigEdit',
        component: OperationalExcellenceConfigEditPage,
        meta: {
          title: 'Edit Partner Alerts | ryd Portal',
          requiresAuth: true,
          requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Operational Excellence Alerts — Edit',
        },
      },
      {
        path: 'operational-excellence/history/:partnerKey?',
        name: 'operationalExcellenceAlertHistory',
        component: OperationalExcellenceHistoryPage,
        meta: {
          title: 'Alert History | ryd Portal',
          requiresAuth: true,
          requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Operational Excellence Alerts — History',
        },
      },
      {
        path: 'operational-alerts',
        name: 'partnerOperationalAlerts',
        component: PartnerOperationalAlertsPage,
        meta: {
          title: 'Operational Alerts | ryd Portal',
          requiresAuth: true,
          requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          excludedPartners: ['bosch'],
          breadcrumb: 'Operational Alerts',
        },
      },
      {
        path: 'alerts',
        name: 'partnerAlerts',
        component: PartnerAlertsPage,
        meta: {
          title: 'Alerts | ryd Portal',
          requiresAuth: true,
          breadcrumb: 'Alerts',
          headTitle: 'Alerts',
        },
      },
      {
        path: 'incidents',
        name: 'partnerIncidents',
        component: PartnerIncidentsPage,
        meta: { title: 'Incidents | ryd Portal', requiresAuth: true, breadcrumb: 'Incidents' },
      },
      {
        path: 'scheduled-maintenance',
        name: 'partnerScheduledMaintenance',
        component: PlatformStatusScheduledMaintenance,
        meta: {
          title: 'Scheduled Maintenance | ryd Portal',
          requiresAuth: true,
          breadcrumb: 'Scheduled Maintenance',
        },
      },
      {
        path: 'announcements',
        name: 'partnerAnnouncements',
        component: PartnerAnnouncementsPage,
        meta: {
          title: 'Announcements | ryd Portal',
          requiresAuth: true,
          breadcrumb: 'Announcements',
        },
      },
      {
        path: 'current-status',
        name: 'platformStatusCurrentStatus',
        component: PlatformStatusCurrentStatus,
        meta: { title: 'Current Status | ryd Portal', breadcrumb: 'Current Status' },
      },
      {
        path: 'system-metrics',
        name: 'platformStatusSystemMetrics',
        component: PlatformStatusSystemMetrics,
        meta: {
          title: 'Platform Status - System Metrics | ryd Portal',
          breadcrumb: 'System Metrics',
        },
      },
      { path: 'past-incidents', redirect: '/platform-status-and-alerts/incidents' },
      {
        path: 'admin',
        component: RoutePassThrough,
        meta: { breadcrumb: 'Admin', ...ADMIN_META },
        children: [
          {
            path: 'alerts/list',
            name: 'adminAlertsList',
            component: AlertsListPage,
            meta: { ...ADMIN_META, breadcrumb: 'Alerts — List', title: 'Alerts | ryd Portal' },
          },
          {
            path: 'alerts/create',
            name: 'adminAlertCreate',
            component: AlertCreatePage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Alerts — Create',
              title: 'Create Alert | ryd Portal',
            },
          },
          {
            path: 'alerts/edit/:id',
            name: 'adminAlertEdit',
            component: AlertEditPage,
            meta: { ...ADMIN_META, breadcrumb: 'Alerts — Edit', title: 'Edit Alert | ryd Portal' },
          },
          {
            path: 'incidents/list',
            name: 'adminIncidentsList',
            component: IncidentsListPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Incidents — List',
              title: 'Incidents | ryd Portal',
            },
          },
          {
            path: 'incidents/create',
            name: 'adminIncidentCreate',
            component: IncidentCreatePage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Incidents — Create',
              title: 'Create Incident | ryd Portal',
            },
          },
          {
            path: 'incidents/edit/:id',
            name: 'adminIncidentEdit',
            component: IncidentEditPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Incidents — Edit',
              title: 'Edit Incident | ryd Portal',
            },
          },
          {
            path: 'scheduled-maintenance/list',
            name: 'adminMaintenanceList',
            component: MaintenanceListPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Scheduled Maintenance — List',
              title: 'Scheduled Maintenance | ryd Portal',
            },
          },
          {
            path: 'scheduled-maintenance/create',
            name: 'adminMaintenanceCreate',
            component: MaintenanceCreatePage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Scheduled Maintenance — Create',
              title: 'Create Maintenance | ryd Portal',
            },
          },
          {
            path: 'scheduled-maintenance/edit/:id',
            name: 'adminMaintenanceEdit',
            component: MaintenanceEditPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Scheduled Maintenance — Edit',
              title: 'Edit Maintenance | ryd Portal',
            },
          },
          {
            path: 'announcements/list',
            name: 'adminAnnouncementsList',
            component: AnnouncementsListPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Announcements — List',
              title: 'Announcements | ryd Portal',
            },
          },
          {
            path: 'announcements/create',
            name: 'adminAnnouncementCreate',
            component: AnnouncementCreatePage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Announcements — Create',
              title: 'Create Announcement | ryd Portal',
            },
          },
          {
            path: 'announcements/edit/:id',
            name: 'adminAnnouncementEdit',
            component: AnnouncementEditPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Announcements — Edit',
              title: 'Edit Announcement | ryd Portal',
            },
          },
          {
            path: 'maintenance-auto-transition',
            name: 'adminMaintenanceAutoTransition',
            component: MaintenanceAutoTransitionPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Maintenance Auto-Transition',
              title: 'Maintenance Auto-Transition | ryd Portal',
            },
          },
          {
            path: 'delivery-history',
            name: 'adminDeliveryHistory',
            component: DeliveryHistoryPage,
            meta: {
              ...ADMIN_META,
              breadcrumb: 'Delivery History',
              title: 'Delivery History | ryd Portal',
            },
          },
          {
            path: 'odoo-delay-monitor',
            redirect: '/accounting/invoicing/odoo-order-monitor',
          },
        ],
      },
    ],
  },
  {
    path: '/alert-account-management/list',
    redirect: '/platform-status-and-alerts/admin/alerts/list',
  },
  {
    path: '/alert-account-management/details',
    redirect: '/platform-status-and-alerts/admin/alerts/list',
  },
  {
    path: '/alert-account-management/create',
    redirect: '/platform-status-and-alerts/admin/alerts/create',
  },
  {
    path: '/backoffice/platform-communications/alerts',
    redirect: '/platform-status-and-alerts/admin/alerts/list',
  },
  {
    path: '/backoffice/platform-communications/incidents',
    redirect: '/platform-status-and-alerts/admin/incidents/list',
  },
  {
    path: '/backoffice/platform-communications/incidents/:incidentId',
    redirect: (to) => `/platform-status-and-alerts/admin/incidents/edit/${to.params.incidentId}`,
  },
  {
    path: '/backoffice/platform-communications/maintenance',
    redirect: '/platform-status-and-alerts/admin/scheduled-maintenance/list',
  },
  {
    path: '/backoffice/platform-communications/maintenance/:maintenanceId',
    redirect: (to) =>
      `/platform-status-and-alerts/admin/scheduled-maintenance/edit/${to.params.maintenanceId}`,
  },
  {
    path: '/backoffice/platform-communications/maintenance-auto-transition',
    redirect: '/platform-status-and-alerts/admin/maintenance-auto-transition',
  },
  {
    path: '/backoffice/platform-communications/delivery-history',
    redirect: '/platform-status-and-alerts/admin/delivery-history',
  },
  { path: '/platform-status', redirect: '/platform-status-and-alerts/overview' },
  {
    path: '/platform-status/current-status',
    redirect: '/platform-status-and-alerts/current-status',
  },
  { path: '/platform-status/past-incidents', redirect: '/platform-status-and-alerts/incidents' },
  {
    path: '/platform-status/scheduled-maintenance',
    redirect: '/platform-status-and-alerts/scheduled-maintenance',
  },
  {
    path: '/platform-status/system-metrics',
    redirect: '/platform-status-and-alerts/system-metrics',
  },
];
